import React, { useMemo, useCallback, useState, useEffect, forwardRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import PropTypes from 'prop-types'

import debounce from 'lodash/debounce'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import Grid from '@material-ui/core/Grid'
import InputAdornment from '@material-ui/core/InputAdornment'
import Typography from '@material-ui/core/Typography'
import SearchIcon from '@material-ui/icons/Search'

import { reloadSchema } from '@galo-monitor/forms'
import machineSchema from '@galo-monitor/forms/schemas/farm/machine.schema'
import { useT } from '@galo-monitor/i18n'
import galoMonitorApi from '@galo-monitor/services/apis/galoMonitorApi'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { MachineActions } from '@galo-monitor/stores/machine'
import { selectCurrentProperty } from '@galo-monitor/stores/property/selectorProperty'
import colors from '@galo-monitor/styles/colors'
import Checkbox from '@galo-monitor/web-components/Checkbox'
import Form from '@galo-monitor/web-components/Form'
import InputFile from '@galo-monitor/web-components/InputFile'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputText from '@galo-monitor/web-components/InputText'
import InputUnit from '@galo-monitor/web-components/InputUnit'

import { PhotoButtonInput } from './theme'

const BRAND_NAME_PARAM_KEY = 'maker-name'
const MACHINE_TYPE_PARAM_KEY = 'machine-type-id'
const DEBOUNCE_TIME_IN_MS = 500

const MachineForm = forwardRef((props, formRef) => {
  const { machine, loading, setLoading, onSuccess, onError } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const { id: currentPropertyId } = useSelector(selectCurrentProperty)

  const [selectedType, setSelectedType] = useState('')
  const [searchBrandParams, setSearchBrandParams] = useState({ [BRAND_NAME_PARAM_KEY]: '' })

  const { data: machineData, isValidating: isValidatingMachineData } = useGaloMonitorApi(machine.id ? `/farms/machines/${ machine.id }` : null)
  const { data: machineTypes } = useGaloMonitorApi('/farms/machines/types')

  const machineTypesOptions = useMemo(
    () => map(machineTypes, ({ name, id }) => ({
      label: t(name),
      value: String(id)
    })),
    [machineTypes, t]
  )

  const handleSubmit = useCallback(
    (data) => {
      setLoading(true)

      const payloadMachineData = machine.id ? { id: machine.id } : {}

      dispatch(MachineActions.saveMachine(
        {
          ...payloadMachineData,
          ...data,
          properties_ids: [String(currentPropertyId)]
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('machine', { howMany: 1 }),
            action: toLower(t(isEmpty(machine) ? 'created' : 'updated', { gender: 'female', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [currentPropertyId, dispatch, machine, onError, onSuccess, setLoading, snackbar, t]
  )

  const handleFetchBrandOptions = params => galoMonitorApi.get('/farms/machines/makers', { params })

  const handleFetchModelOptions = useCallback(
    params => {
      if (selectedType && searchBrandParams[BRAND_NAME_PARAM_KEY])
        return galoMonitorApi.get('/farms/machines/models', { params })
      return new Promise(resolve => resolve({ data: [] }))
    },
    [searchBrandParams, selectedType]
  )

  const handleMakerKeyUp = debounce(
    value => {
      setSearchBrandParams({
        [BRAND_NAME_PARAM_KEY]: value
      })
    },
    DEBOUNCE_TIME_IN_MS
  )

  useEffect(() => {
    if (!isEmpty(machineData)) {
      formRef.current.setData({
        name: machineData.name,
        machine_type_id: String(machineData.machineTypeId),
        maker_name: machineData.maker,
        model_name: machineData.model,
        potential_motor: String(machineData.potential),
        year_fabrication: String(machineData.yearFabrication),
        price: String(machineData.price),
        is_motorized: machineData.isMotorized
      })
      setLoading(false)
    }
  }, [formRef, isValidatingMachineData, machineData, setLoading])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => machine.id && setLoading(true), [])

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ machineSchema }
      onSubmit={ handleSubmit }
    >
      <Grid container spacing={ 3 }>
        <Grid item xs={ 12 } sm={ 6 }>
          <InputText
            name="name"
            label={ t('name of {this}', { this: t('machine', { howMany: 1 }), howMany: 1, gender: 'female' }) }
            disabled={ loading }
            fullWidth
          />
        </Grid>
        <Grid item xs={ 12 } sm={ 6 }>
          <InputSelect
            name="machine_type_id"
            label={ t('type', { howMany: 1 }) }
            onFieldValueChange={ setSelectedType }
            options={ machineTypesOptions }
            disabled={ loading }
            fullWidth
          />
        </Grid>
        <Grid item xs={ 12 } sm={ 6 }>
          <InputSelect
            name="maker_name"
            label={ t('brand', { howMany: 1 }) }
            onKeyUp={ evt => handleMakerKeyUp(evt.target.value) }
            options={ handleFetchBrandOptions }
            queryParams={ searchBrandParams }
            asyncOptionValueField="name"
            disabled={ loading }
            InputProps={ {
              endAdornment: (
                <InputAdornment position="end" style={ { marginRight: -25 } }>
                  <SearchIcon style={ { color: colors.lightGrey4 } } />
                </InputAdornment>
              )
            } }
            creatable
            disableClearable
            fullWidth
          />
        </Grid>
        <Grid item xs={ 12 } sm={ 6 }>
          <InputSelect
            name="model_name"
            label={ t('model', { howMany: 1 }) }
            options={ handleFetchModelOptions }
            queryParams={ { ...searchBrandParams, [MACHINE_TYPE_PARAM_KEY]: selectedType } }
            asyncOptionValueField="name"
            disabled={ loading }
            InputProps={ {
              endAdornment: (
                <InputAdornment position="end" style={ { marginRight: -25 } }>
                  <SearchIcon style={ { color: colors.lightGrey4 } } />
                </InputAdornment>
              )
            } }
            creatable
            disableClearable
            fullWidth
          />
        </Grid>
        <Grid item xs={ 12 } sm={ 6 }>
          <Checkbox
            label={ t('motorized equipment') }
            name="is_motorized"
            color="primary"
            onChange={ () => reloadSchema(formRef, true) }
            disabled={ loading }
          />
        </Grid>
        <Grid item xs={ 12 } sm={ 6 }>
          <InputUnit
            label={ t('potency') }
            name="potential_motor"
            unit={ t('horse power unit') }
            type="integer"
            fullWidth
          />
        </Grid>
        <Grid item xs={ 12 } sm={ 6 }>
          <InputText
            name="year_fabrication"
            label={ t('fabrication year') }
            disabled={ loading }
            fullWidth
          />
        </Grid>
        <Grid item xs={ 12 } sm={ 6 }>
          <InputUnit
            label={ t('approximate value') }
            name="price"
            unit="$"
            defaultValue={ machine.price }
            fullWidth
          />
        </Grid>
        <Grid item xs={ 12 } sm={ 6 }>
          <Typography variant="h6">
            {t('photo', { howMany: 1 })}
          </Typography>
          <InputFile
            name="photo"
            InputComponent={ (inputProps) => (
              <div style={ {
                width: 'max-content',
                cursor: 'not-allowed',
                opacity: 0.5
              } }
              >
                <PhotoButtonInput { ...inputProps } />
              </div>
            ) }
            disabled
          />
        </Grid>
      </Grid>
    </Form>
  )
})

MachineForm.propTypes = {
  machine: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

MachineForm.defaultProps = {
  machine: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default MachineForm
